import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import { SessionRecorder, TouchTracker, detectMultiTouch } from "@talent/client-telemetry";
import { flipBudget, scorePairsTrial, type PairsBoard, DEMO_FORM } from "@talent/tasks";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  easeOutCubic,
  pop,
  prefersReducedMotion,
  ripple,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { drawToken } from "./token.js";
import { COLOR, familyColor, mix, touchMin } from "./paint.js";

/**
 * Hidden pairs — turn two cards over and find the match.
 *
 * **A card turned back over is turned back slowly.** The temptation is to snap it shut the instant
 * the pair fails, because it makes the game brisk. It also destroys the task: the second or so a
 * child spends looking at two unmatched cards is when they commit them to memory, and taking it
 * away leaves a game that can only be solved by luck.
 *
 * **Nothing is ever hidden that the child has not seen.** No shuffling between turns, no cards
 * moving. The board a child builds a memory of has to be the board that is still there.
 */

export interface PairsTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generateBoard: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => PairsBoard;
  showTutorial?: boolean;
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

interface Attempt {
  flips: number;
}

export async function runPairsTask(opts: RunOptions): Promise<PairsTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);
  const idle = (ms: number) => wait(ms, opts.signal);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    for (let i = 0; i < 24; i++) {
      backdrop.rect(0, (h / 24) * i, w, h / 24 + 1).fill(mix(COLOR.stage, COLOR.stageDeep, i / 24));
    }
    backdrop.circle(w / 2, h * 0.45, Math.min(w, h) * 0.52).fill({ color: accent, alpha: 0.07 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  function present(board: PairsBoard): {
    attempt: Promise<Attempt>;
    simulateTap: (index: number) => void;
    relayout: () => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);

    const faceUp = new Set<number>();
    const matched = new Set<number>();
    let selected: number[] = [];
    let flips = 0;
    let busy = false;
    let accepting = true;
    let resolveAttempt: ((a: Attempt) => void) | null = null;

    const cards: Graphics[] = [];
    let cardSize = 0;

    /** The back of a card: the family colour with a soft mark, so a board reads as cards. */
    function drawCard(g: Graphics, index: number): void {
      const size = cardSize;
      g.clear();
      const isUp = faceUp.has(index) || matched.has(index);

      if (!isUp) {
        g.roundRect(-size / 2, -size / 2, size, size, size * 0.22).fill(
          mix(COLOR.stage, accent, 0.35),
        );
        g.roundRect(-size / 2 + size * 0.08, -size / 2 + size * 0.07, size * 0.84, size * 0.14, size * 0.08)
          .fill({ color: 0xffffff, alpha: 0.1 });
        g.circle(0, 0, size * 0.16).fill({ color: 0xffffff, alpha: 0.18 });
        return;
      }

      const token = board.cards[index];
      drawToken(g, token ?? null, size, matched.has(index) ? "found" : "idle", accent);
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const gap = Math.max(8, w * 0.025);
      const topReserved = 64;
      const bottomReserved = 44;

      cardSize = Math.max(
        touchMin(),
        Math.min(
          (w * 0.9 - gap * (board.cols - 1)) / board.cols,
          (h - topReserved - bottomReserved - gap * (board.rows - 1)) / board.rows,
        ),
      );
      const gridW = cardSize * board.cols + gap * (board.cols - 1);
      const gridH = cardSize * board.rows + gap * (board.rows - 1);
      const originX = (w - gridW) / 2 + cardSize / 2;
      const originY = topReserved + (h - topReserved - bottomReserved - gridH) / 2 + cardSize / 2;

      layer.removeChildren().forEach((c) => c.destroy());
      cards.length = 0;

      for (let i = 0; i < board.cards.length; i++) {
        const g = new Graphics();
        g.position.set(
          originX + (i % board.cols) * (cardSize + gap),
          originY + Math.floor(i / board.cols) * (cardSize + gap),
        );
        g.eventMode = "static";
        g.cursor = "pointer";
        const half = cardSize / 2 + cardSize * 0.08;
        g.hitArea = {
          contains: (x: number, y: number) => x >= -half && y >= -half && x <= half && y <= half,
        };
        g.on("pointertap", () => turn(i));
        drawCard(g, i);
        cards.push(g);
        layer.addChild(g);
      }
    }

    /** Flips a card, half-turn by half-turn, so the reveal reads as a card turning over. */
    async function flipTo(index: number, faceUpAfter: boolean): Promise<void> {
      const g = cards[index];
      if (!g || g.destroyed) return;
      if (prefersReducedMotion()) {
        if (faceUpAfter) faceUp.add(index);
        else faceUp.delete(index);
        drawCard(g, index);
        return;
      }
      await animate(130, (t) => {
        if (!g.destroyed) g.scale.x = 1 - easeOutCubic(t);
      });
      if (faceUpAfter) faceUp.add(index);
      else faceUp.delete(index);
      if (!g.destroyed) drawCard(g, index);
      await animate(150, (t) => {
        if (!g.destroyed) g.scale.x = easeOutBack(t);
      });
      if (!g.destroyed) g.scale.x = 1;
    }

    function turn(index: number): void {
      if (!accepting || busy) return;
      if (matched.has(index) || faceUp.has(index)) {
        // Acknowledged rather than ignored: an unanswered tap reads as broken and gets tapped
        // again, which the contamination detectors then see as a multi-touch anomaly.
        tick();
        return;
      }

      const g = cards[index];
      if (!g) return;
      busy = true;
      flips++;
      cellTone(index + 2);
      ripple(fx, g.position.x, g.position.y, accent, cardSize);

      void (async () => {
        await flipTo(index, true);
        selected.push(index);

        if (selected.length < 2) {
          busy = false;
          return;
        }

        const [a, b] = selected as [number, number];
        const isPair = board.cards[a] === board.cards[b];
        selected = [];

        if (isPair) {
          matched.add(a);
          matched.add(b);
          faceUp.delete(a);
          faceUp.delete(b);
          successChime();
          for (const i of [a, b]) {
            const card = cards[i];
            if (!card) continue;
            drawCard(card, i);
            burst(fx, card.position.x, card.position.y, [accent, COLOR.correct], 10);
            void pop(card, { scale: 1.14 });
          }
          await idle(420);
          busy = false;

          if (matched.size === board.cards.length) {
            accepting = false;
            await idle(320);
            resolveAttempt?.({ flips });
          }
          return;
        }

        missTone();
        /**
         * Held before turning back, and the length of the hold is the task.
         *
         * Snapping them shut makes the game brisk and unplayable: this pause is when a child
         * commits two positions to memory, and without it the board can only be cleared by luck.
         */
        await idle(1050);
        await Promise.all([flipTo(a, false), flipTo(b, false)]);
        busy = false;
      })();
    }

    layout();
    /**
     * The pieces arrive rather than appearing.
     *
     * A board that is simply there reads as a diagram; one that is dealt out reads as a game being set
     * up for you — and it gives a child a moment to look before anything is expected of them. Staggered
     * by index so it is a sequence, and skipped entirely under `prefers-reduced-motion`, where the whole
     * point of the setting is that things do not fly around.
     */
    if (!prefersReducedMotion()) {
      cards.forEach((card, i) => {
        card.scale.set(0);
        void idle(60 + i * 40).then(() => animate(260, (t) => card.scale.set(easeOutBack(t))));
      });
    }

    return {
      attempt: new Promise<Attempt>((resolve) => {
        resolveAttempt = resolve;
      }),
      simulateTap: turn,
      relayout: layout,
      cleanup: () => {
        accepting = false;
        layer.destroy({ children: true });
      },
    };
  }

  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctRounds = 0;
  let reason: PairsTaskResult["reason"] = "completed";
  let onResize: (() => void) | null = null;

  /**
   * A wordless demonstration: two cards turned over, matched, and left face up.
   *
   * Runs on the easiest board so the pair is adjacent and the point is unmissable. It shows the
   * mechanic — turn two, they stay if they match — and nothing about strategy, which is the part
   * worth observing.
   */
  async function playTutorial(): Promise<void> {
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generateBoard(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const first = demo.cards[0];
      const partner = demo.cards.findIndex((t, i) => i !== 0 && t === first);
      const shown = present(demo);
      await idle(800);
      if (!opts.signal?.aborted && partner > 0) {
        shown.simulateTap(0);
        await idle(900);
        shown.simulateTap(partner);
        await idle(1100);
      }
      if (!opts.signal?.aborted) {
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const board = opts.generateBoard(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The board as dealt, so the round is re-scorable from the record alone.
        stimulus: board.cards,
      });

      const start = performance.now();
      const trial = present(board);
      onResize = trial.relayout;
      app.renderer.on("resize", trial.relayout);

      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - start;
      app.renderer.off("resize", trial.relayout);
      onResize = null;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      const passed = scorePairsTrial(board, outcome.flips);
      if (passed) correctRounds++;

      await recorder.record("trial_response", {
        trialIndex,
        // The board always clears, so the response IS the cost of clearing it. A child holding
        // four positions finishes near the minimum; one holding none brute-forces to the budget,
        // and both end with the same cleared board.
        response: [outcome.flips, flipBudget(board)],
        correct: passed,
        responseMs: elapsed,
      });

      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, passed);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(360);
    }
  } catch {
    reason = "error";
  } finally {
    if (onResize) app.renderer.off("resize", onResize);
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctRounds / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctRounds,
    rawScore: trialIndex > 0 ? correctRounds / trialIndex : 0,
  };
}
